import React from "react";
import {
  Stethoscope,
  Syringe,
  Scissors,
  Activity,
  Siren,
  HeartPulse,
  ArrowUpRight,
} from "lucide-react";
import { waLink } from "../lib/contact";

const SERVICES = [
  {
    icon: Stethoscope,
    title: "Consultas Generales",
    desc: "Chequeos y diagnósticos completos para mantener a tu mascota sana en cada etapa de su vida.",
    accent: "#84B891",
    bg: "#E3F0E6",
    testid: "service-consulta",
  },
  {
    icon: Syringe,
    title: "Vacunación",
    desc: "Plan de vacunas al día contra las enfermedades más comunes en perros y gatos.",
    accent: "#F2A78C",
    bg: "#FCEEDF",
    testid: "service-vacunacion",
  },
  {
    icon: HeartPulse,
    title: "Cirugías",
    desc: "Procedimientos quirúrgicos seguros con monitoreo constante y recuperación cuidadosa.",
    accent: "#7BA8E0",
    bg: "#E2EDFB",
    testid: "service-cirugias",
  },
  {
    icon: Scissors,
    title: "Peluquería",
    desc: "Baño, corte y estética para que tu mascota se sienta tan bien como se ve.",
    accent: "#C794D6",
    bg: "#F1E4F6",
    testid: "service-peluqueria",
  },
  {
    icon: Activity,
    title: "Exámenes & Laboratorio",
    desc: "Análisis clínicos y exámenes para diagnósticos rápidos y precisos.",
    accent: "#84B891",
    bg: "#E3F0E6",
    testid: "service-laboratorio",
  },
  {
    icon: Siren,
    title: "Urgencias 24/7",
    desc: "Emergencias atendidas a cualquier hora del día y la noche. Tu llamada no espera.",
    accent: "#FF5A5F",
    bg: "#FFE4E6",
    highlight: true,
    testid: "service-urgencias",
  },
];

export const Services = () => {
  return (
    <section
      id="servicios"
      data-testid="services-section"
      className="relative py-20 sm:py-28"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-2xl mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E3F0E6] text-[#4A5E4F] text-xs font-bold tracking-widest uppercase mb-5">
            Nuestros servicios
          </div>
          <h2 className="font-heading font-semibold text-[#2D3A30] text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
            Todo lo que tu mascota necesita,
            <span className="text-[#F28C8C]"> en un solo lugar.</span>
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[#4A5E4F] leading-relaxed">
            Desde controles de rutina hasta urgencias críticas, nuestro equipo
            está preparado para entregarle a tu compañero el mejor cuidado.
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 lg:gap-8">
          {SERVICES.map((service, idx) => {
            const Icon = service.icon;
            const isHighlight = service.highlight;
            return (
              <div
                key={service.title}
                data-testid={service.testid}
                className={`group relative overflow-hidden rounded-3xl p-6 sm:p-8 transition-all duration-300 hover:-translate-y-1 fade-up ${
                  isHighlight
                    ? "bg-gradient-to-br from-[#FFE4E6] to-white border-2 border-[#FF5A5F]/40 shadow-[0_10px_40px_rgba(255,90,95,0.15)]"
                    : "bg-white border border-[#D1E2D6] shadow-[0_8px_30px_rgba(45,58,48,0.06)] hover:shadow-[0_14px_40px_rgba(45,58,48,0.10)]"
                }`}
                style={{ animationDelay: `${idx * 60}ms` }}
              >
                {isHighlight && (
                  <div className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FF5A5F] text-white text-[10px] font-bold tracking-widest uppercase">
                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                    24/7
                  </div>
                )}
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 transition-transform duration-300 group-hover:rotate-6 group-hover:scale-110"
                  style={{ backgroundColor: service.bg }}
                >
                  <Icon
                    className="w-7 h-7"
                    style={{ color: service.accent }}
                    strokeWidth={2}
                  />
                </div>
                <h3 className="font-heading font-semibold text-[#2D3A30] text-xl sm:text-2xl mb-2">
                  {service.title}
                </h3>
                <p className="text-[#4A5E4F] text-sm sm:text-base leading-relaxed">
                  {service.desc}
                </p>

                {isHighlight && (
                  <a
                    href={waLink(
                      "Hola Royal Pets, tengo una urgencia con mi mascota."
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="service-urgencias-cta"
                    className="mt-5 inline-flex items-center gap-2 text-[#FF5A5F] font-bold text-sm hover:gap-3 transition-all"
                  >
                    Contactar urgencia
                    <ArrowUpRight className="w-4 h-4" />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
